function normalizeSourceId(item) {
  if (typeof item === 'string') {
    return String(item).trim();
  }
  if (item && typeof item === 'object' && !Array.isArray(item)) {
    return String(item.source_id || item.id || '').trim();
  }
  return '';
}

function normalizeMetadataObject(value) {
  return (
    value
    && typeof value === 'object'
    && !Array.isArray(value)
  ) ? { ...value } : null;
}

function firstTruthyTextValue(...values) {
  const value = values.find(Boolean);
  return String(value || '').trim();
}

function compactMetadata(entries) {
  return Object.fromEntries(entries.filter(([, value]) => Boolean(value)));
}

function buildSourceDisplayMetadata(record, documentProfile, documentUnderstanding) {
  return compactMetadata([
    ['title', firstTruthyTextValue(
      record.title,
      documentProfile?.display_name,
      record.filename,
      record.name,
    )],
    ['filename', firstTruthyTextValue(record.filename, record.name, documentProfile?.filename)],
    ['mime_type', firstTruthyTextValue(record.mime_type, record.type, documentProfile?.mime_type)],
    ['document_profile', documentProfile],
    ['document_understanding', documentUnderstanding],
  ]);
}

function buildSourceLifecycleMetadata(record) {
  return compactMetadata([
    ['status', String(record.status || '').trim().toLowerCase()],
    ['scope', String(record.scope || '').trim()],
    ['error_code', String(record.error_code || '').trim()],
    ['error_message', String(record.error_message || '').trim()],
  ]);
}

function buildSourceReference(item, { includeLifecycle = false } = {}) {
  const sourceId = normalizeSourceId(item);
  if (!sourceId) {
    return null;
  }
  const ref = { source_id: sourceId };
  const record = normalizeMetadataObject(item);
  if (!record) {
    return ref;
  }
  const documentProfile = normalizeMetadataObject(record.document_profile);
  const documentUnderstanding = normalizeMetadataObject(record.document_understanding);
  const displayMetadata = buildSourceDisplayMetadata(
    record,
    documentProfile,
    documentUnderstanding,
  );
  return {
    ...ref,
    ...displayMetadata,
    ...(includeLifecycle ? buildSourceLifecycleMetadata(record) : {}),
  };
}

function mergeSourceReferenceListsWithOptions(options = {}, ...lists) {
  const refs = [];
  const seen = new Set();
  lists.forEach((list) => {
    if (!Array.isArray(list)) {
      return;
    }
    list.forEach((item) => {
      const ref = buildSourceReference(item, options);
      const sourceId = ref?.source_id || '';
      if (!sourceId || seen.has(sourceId)) {
        return;
      }
      seen.add(sourceId);
      refs.push(ref);
    });
  });
  return refs;
}

export function mergeSourceReferenceLists(...lists) {
  return mergeSourceReferenceListsWithOptions({}, ...lists);
}

export function collectComposerAttachmentLifecycleRefs(fileList = []) {
  if (!Array.isArray(fileList)) {
    return [];
  }
  return mergeSourceReferenceListsWithOptions({ includeLifecycle: true }, fileList);
}

export function collectSourceIdStrings(...lists) {
  const ids = [];
  const seen = new Set();
  lists.forEach((list) => {
    if (!Array.isArray(list)) {
      return;
    }
    list.forEach((item) => {
      const sourceId = normalizeSourceId(item);
      if (!sourceId || seen.has(sourceId)) {
        return;
      }
      seen.add(sourceId);
      ids.push(sourceId);
    });
  });
  return ids;
}

function normalizeAttachmentStatus(item) {
  return String(item?.status || '').trim().toLowerCase();
}

function isTerminalAttachmentFailure(status) {
  return status === 'error' || status.startsWith('failed');
}

export function collectSendableComposerAttachments(fileList = []) {
  if (!Array.isArray(fileList)) {
    return [];
  }
  return fileList
    .filter((item) => (
      normalizeAttachmentStatus(item) === 'ready'
      && Boolean(normalizeSourceId(item))
    ))
    .map((item) => ({ ...item }));
}

function normalizeSourceScope(item) {
  const explicitScope = String(item?.scope || '').trim().toLowerCase();
  if (explicitScope) {
    return explicitScope;
  }
  return firstTruthyTextValue(item?.session_id, item?.sessionId) ? 'session' : 'project';
}

export function collectReadyAttachmentRefs(fileList = []) {
  const readyRefs = collectSendableComposerAttachments(fileList);
  return mergeSourceReferenceLists(readyRefs);
}

/**
 * DOC HELPER: Project sources are ambient context candidates.
 * Ready sources from the current project should be available to the turn
 * without asking users to manually attach/reference each file in composer.
 */
export function collectReadyProjectSourceRefs(sourceItems = []) {
  if (!Array.isArray(sourceItems)) {
    return [];
  }
  const readyRefs = sourceItems.filter((item) => {
    const sourceId = normalizeSourceId(item);
    if (!sourceId) {
      return false;
    }
    return normalizeSourceScope(item) === 'project'
      && String(item?.status || '').trim().toLowerCase() === 'ready';
  });
  return mergeSourceReferenceLists(readyRefs);
}

export function hasNonReadyComposerAttachments(fileList = []) {
  return hasBlockingComposerAttachments(fileList);
}

export function hasBlockingComposerAttachments(fileList = []) {
  if (!Array.isArray(fileList)) {
    return false;
  }
  return fileList.some((item) => {
    const sourceId = normalizeSourceId(item);
    const status = normalizeAttachmentStatus(item);
    if (isTerminalAttachmentFailure(status)) {
      return false;
    }
    return Boolean(!sourceId || status !== 'ready');
  });
}

function buildAttachmentSnapshot(fileList = []) {
  if (!Array.isArray(fileList)) {
    return [];
  }
  return fileList.map((item) => ({ ...item }));
}

function resolveUploadableFile(item) {
  if (!item || typeof item !== 'object' || Array.isArray(item)) {
    return null;
  }
  const sourceId = String(item.source_id || '').trim();
  if (sourceId) {
    return null;
  }
  const status = normalizeAttachmentStatus(item);
  if (isTerminalAttachmentFailure(status)) {
    return null;
  }
  return item.originFileObj || null;
}

function buildFailedAttachment(attachment, errorMessage = '') {
  return {
    ...attachment,
    status: 'failed',
    error_message: String(errorMessage || ''),
  };
}

function buildAttachmentUploadInput({
  attachment,
  projectId,
  sessionId,
  uploadableFile,
}) {
  const resolvedSessionId = String(sessionId || '').trim();
  return {
    file: uploadableFile,
    filename: firstTruthyTextValue(
      attachment?.name,
      attachment?.filename,
      uploadableFile?.name,
      'upload.bin',
    ),
    project_id: projectId,
    session_id: resolvedSessionId || null,
    scope: 'session',
  };
}

function resolveUploadedAttachment(attachment, uploaded) {
  const sourceId = normalizeSourceId(uploaded);
  if (!sourceId) {
    return buildFailedAttachment(attachment);
  }
  return {
    ...attachment,
    ...uploaded,
    source_id: sourceId,
    status: String(uploaded?.status || attachment?.status || 'ready'),
    error_message: String(uploaded?.error_message || ''),
  };
}

async function uploadComposerAttachment({
  attachment,
  projectId,
  sessionId,
  uploadSourceFile,
}) {
  const uploadableFile = resolveUploadableFile(attachment);
  if (!uploadableFile) {
    return attachment;
  }
  try {
    const uploaded = await uploadSourceFile(buildAttachmentUploadInput({
      attachment,
      projectId,
      sessionId,
      uploadableFile,
    }));
    return resolveUploadedAttachment(attachment, uploaded);
  } catch (error) {
    return buildFailedAttachment(attachment, firstTruthyTextValue(error?.message, error));
  }
}

export async function prepareComposerAttachmentSendRefs({
  fileList = [],
  uploadSourceFile,
  projectId = '',
  sessionId = '',
} = {}) {
  const attachedSessionFilesSnapshot = buildAttachmentSnapshot(fileList);
  const resolvedProjectId = String(projectId || '').trim();
  const canUpload = typeof uploadSourceFile === 'function' && Boolean(resolvedProjectId);
  const preparedAttachmentSnapshot = canUpload
    ? await Promise.all(attachedSessionFilesSnapshot.map((attachment) => uploadComposerAttachment({
      attachment,
      projectId: resolvedProjectId,
      sessionId,
      uploadSourceFile,
    })))
    : attachedSessionFilesSnapshot;

  const refs = mergeSourceReferenceLists(
    collectSendableComposerAttachments(preparedAttachmentSnapshot),
  );
  return {
    attachedSessionFilesSnapshot: preparedAttachmentSnapshot,
    refs,
  };
}
